/**
 * ImportFilesStep
 *
 * The "Import files" starting point of the new-diagram dialog (task 5.5,
 * Requirement 16). Lets the user pick or drop one or more existing Excalidraw
 * files (`.excalidraw`, or `.svg`/`.png` exported with the scene embedded),
 * reviews each one in a row with an editable title and a status, and — on
 * confirm — creates one diagram per accepted file, in order, into the single
 * Diagram_Folder chosen in the dialog.
 *
 * Per-file flow, matching the new-diagram create flow but using the imported
 * scene (Requirement 16.4): reserve an id → export the imported scene as a
 * Diagram_File with that id and the row's title → create with the import commit
 * message `Import "{title}" from {file name}`, retrying once on 409 `ID_TAKEN`.
 * A failure (including a file over the 2 MB limit, surfaced as a 413 by the
 * backend) marks that row failed and does not stop the others (Requirement
 * 16.5). Files are read with `readImportFile`, which rejects libraries and
 * scene-less files with the fixed messages in Requirement 16.2.
 *
 * Endings (Requirement 16.6): if exactly one diagram was created, navigate to
 * it in the editor; if several, stay on the browser filtered to the folder and
 * show a summary toast. Both are delegated to the parent via `onImported`,
 * because navigation and the browser's folder filter live there.
 */

import { useCallback, useMemo, useState } from "react"
import { useDropzone, type FileRejection } from "react-dropzone"
import { CheckCircle2, Loader2, Upload, XCircle } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/hooks/useAuth"
import {
  createDiagram,
  reserveDiagramId,
  DiagramsApiError,
} from "@/services/diagramsService"
import { exportDiagramSvg, type DiagramScene } from "@/components/diagrams/diagramSvg"
import {
  ACCEPTED_IMPORT_EXTENSIONS,
  ImportRejectedError,
  IMPORT_REJECTION_MESSAGES,
  readImportFile,
  titleFromFileName,
} from "@/components/diagrams/importDiagrams"
import { validateTitle } from "@/components/diagrams/newDiagramForm"

/**
 * The lifecycle status of one file row (Requirement 16.5):
 *   - `"rejected"` — the file is not an importable diagram; it is listed with
 *     its reason and is never created;
 *   - `"waiting"` — accepted and queued, not yet attempted;
 *   - `"creating"` — its create call is in flight;
 *   - `"created"` — committed to the repo;
 *   - `"failed"` — its create call failed; listed with the error.
 */
type ImportRowStatus = "rejected" | "waiting" | "creating" | "created" | "failed"

/**
 * One row in the import list: a chosen file plus its editable title and
 * progress. Rejected files keep their `reason` so the fixed message shows even
 * though they will not be created.
 */
interface ImportRow {
  /** A stable key for React lists and row updates (file name + index). */
  key: string
  /** The original file (read lazily when the row is created). */
  file: File
  /** The editable Diagram_Title; defaults from the file name (Requirement 16.3). */
  title: string
  /** The row's current lifecycle status. */
  status: ImportRowStatus
  /**
   * The message to show for a rejected or failed row: the fixed rejection text
   * (Requirement 16.2) or the create error. Null while waiting/creating/created.
   */
  message: string | null
  /** The created Diagram_Id, set once the row reaches "created". */
  createdId: string | null
}

/** Props for the ImportFilesStep component. */
interface ImportFilesStepProps {
  /**
   * The single Diagram_Folder all imported diagrams go into, or null when the
   * import is unfiled. Chosen in the dialog's shared folder field, validated
   * there; passed straight through to `createDiagram`.
   */
  folder: string | null
  /** Whether the step's inputs are disabled (the dialog is mid-submit). */
  disabled: boolean
  /**
   * Called when the import run finishes. The parent decides the ending
   * (Requirement 16.6): navigate to the single created diagram, or stay on the
   * browser filtered to `folder`. The summary toast for several files is shown
   * here; the parent only needs the outcome to close and route.
   *
   * @param outcome - The import outcome.
   * @param outcome.createdIds - The ids created this run, in creation order.
   * @param outcome.folder - The folder the diagrams went into (echoed back).
   */
  onImported: (outcome: { createdIds: string[]; folder: string | null }) => void
}

/**
 * Build the import commit message for a file (Requirement 2.7):
 * `Import "{title}" from {original file name}`.
 *
 * @param params - The message inputs.
 * @param params.title - The Diagram_Title chosen for the imported diagram.
 * @param params.fileName - The original file name it was imported from.
 * @returns The commit message string.
 */
function buildImportCommitMessage({
  title,
  fileName,
}: {
  title: string
  fileName: string
}): string {
  return `Import "${title}" from ${fileName}`
}

/**
 * Read one accepted file, export it as a Diagram_File, and create the diagram,
 * retrying once with a fresh id on 409 `ID_TAKEN` (the reserve/create race).
 *
 * The imported scene keeps its own elements, images, and canvas settings; only
 * `exportDiagramSvg`'s fixed export settings are applied (Requirement 16.4).
 *
 * @param params - The create inputs.
 * @param params.token - The Auth0 access token for the API calls.
 * @param params.file - The file to import.
 * @param params.title - The trimmed Diagram_Title for the new diagram.
 * @param params.folder - The Diagram_Folder, or null when unfiled.
 * @returns The created Diagram_Id.
 * @throws ImportRejectedError if the file is a library or has no scene.
 * @throws DiagramsApiError or Error if the create fails (after the one retry).
 */
async function importOneFile({
  token,
  file,
  title,
  folder,
}: {
  token: string
  file: File
  title: string
  folder: string | null
}): Promise<string> {
  // Read the file first; a rejection here (library / no scene) propagates to
  // the caller to mark the row rejected, and is thrown before any id is spent.
  const scene: DiagramScene = await readImportFile(file)
  const commitMessage: string = buildImportCommitMessage({ title, fileName: file.name })

  // One attempt: reserve an id, export the imported scene with it, create.
  const attempt = async (): Promise<string> => {
    const id: string = await reserveDiagramId(token)
    const svg: string = await exportDiagramSvg({ scene, id, title })
    const created = await createDiagram(token, {
      id,
      kind: "excalidraw",
      title,
      folder,
      svg,
      commitMessage,
    })
    return created.id
  }

  try {
    return await attempt()
  } catch (caught: unknown) {
    // The reserved id was taken between reserve and create: retry once. Any
    // other failure (or a second clash) is for the caller to mark as failed.
    if (caught instanceof DiagramsApiError && caught.code === "ID_TAKEN") {
      return attempt()
    }
    throw caught
  }
}

/**
 * The import step: file picker and drop zone, per-file rows, and the sequential
 * create run.
 *
 * @param props - See {@link ImportFilesStepProps}.
 * @returns The rendered import step.
 */
export default function ImportFilesStep({
  folder,
  disabled,
  onImported,
}: ImportFilesStepProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  const [rows, setRows] = useState<ImportRow[]>([])
  // True while the sequential create run is in flight; locks the picker, titles,
  // and the folder (owned by the parent) so the run operates on a stable list.
  const [importing, setImporting] = useState<boolean>(false)

  /**
   * Add dropped or picked files to the list. Accepted files become "waiting"
   * rows with a title derived from the file name; files react-dropzone rejected
   * by extension become "rejected" rows so the user sees why (Requirement
   * 16.2's spirit: nothing silently dropped).
   *
   * @param accepted - Files whose extension matched the accept filter.
   * @param fileRejections - Files react-dropzone rejected (wrong extension).
   */
  const addFiles = useCallback(
    (accepted: File[], fileRejections: FileRejection[]): void => {
      setRows((previous: ImportRow[]): ImportRow[] => {
        const base: number = previous.length
        const acceptedRows: ImportRow[] = accepted.map(
          (file: File, offset: number): ImportRow => {
            const derived: string = titleFromFileName(file.name)
            return {
              key: `${file.name}-${base + offset}`,
              file,
              // Fall back to the bare file name when the name was all
              // extensions, so the row still has an editable starting title.
              title: derived === "" ? file.name : derived,
              status: "waiting",
              message: null,
              createdId: null,
            }
          },
        )
        const rejectedRows: ImportRow[] = fileRejections.map(
          ({ file }: FileRejection, offset: number): ImportRow => ({
            key: `${file.name}-rej-${base + accepted.length + offset}`,
            file: file as File,
            title: titleFromFileName(file.name),
            status: "rejected",
            // Wrong extension is a kind of "not a diagram"; reuse the no-scene
            // wording so the user gets a clear, consistent reason.
            message: IMPORT_REJECTION_MESSAGES["no-scene"],
            createdId: null,
          }),
        )
        return [...previous, ...acceptedRows, ...rejectedRows]
      })
    },
    [],
  )

  const onDrop = useCallback(
    (accepted: File[], fileRejections: FileRejection[]): void => {
      addFiles(accepted, fileRejections)
    },
    [addFiles],
  )

  // Accept by extension (react-dropzone keys `accept` by MIME type, with
  // extension lists as values; an empty MIME maps the extensions through).
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    disabled: disabled || importing,
    noClick: true,
    noKeyboard: true,
    accept: { "": [...ACCEPTED_IMPORT_EXTENSIONS] },
  })

  /**
   * Update a single row by key, leaving the rest untouched.
   *
   * @param key - The row key to update.
   * @param patch - The partial fields to merge into that row.
   */
  const updateRow = useCallback((key: string, patch: Partial<ImportRow>): void => {
    setRows((previous: ImportRow[]): ImportRow[] =>
      previous.map((row: ImportRow): ImportRow =>
        row.key === key ? { ...row, ...patch } : row,
      ),
    )
  }, [])

  /**
   * Change a row's editable title.
   *
   * @param key - The row key to update.
   * @param title - The new title text.
   */
  const handleTitleChange = useCallback(
    (key: string, title: string): void => {
      updateRow(key, { title })
    },
    [updateRow],
  )

  // The rows eligible for creation: accepted and not already created. Used both
  // to drive the run and to enable the Import button.
  const importableRows = useMemo<ImportRow[]>(
    () =>
      rows.filter(
        (row: ImportRow): boolean => row.status === "waiting" || row.status === "failed",
      ),
    [rows],
  )

  // Every importable row must have a valid title before the run can start, so a
  // bad title fails inline here rather than as a 400 from the backend.
  const allTitlesValid: boolean = importableRows.every(
    (row: ImportRow): boolean => validateTitle(row.title) === null,
  )

  /**
   * Run the import: create one diagram per importable row, in order, continuing
   * past failures (Requirement 16.5), then hand the outcome to the parent for
   * the ending (Requirement 16.6).
   */
  const handleImport = useCallback(async (): Promise<void> => {
    if (importableRows.length === 0) {
      return
    }
    setImporting(true)

    let token: string
    try {
      token = await getAccessToken()
    } catch (caught: unknown) {
      // Without a token nothing can be created; say so and unlock the dialog
      // rather than leave it stuck in "importing".
      setImporting(false)
      toast.error(
        caught instanceof Error
          ? `Could not start the import: ${caught.message}`
          : "Could not start the import.",
      )
      return
    }
    const createdIds: string[] = []

    // Snapshot the keys to process now; new rows added mid-run are left for a
    // later click rather than racing the loop.
    const toProcess: ImportRow[] = importableRows

    for (const row of toProcess) {
      const trimmedTitle: string = row.title.trim()
      if (validateTitle(trimmedTitle) !== null) {
        // Shouldn't happen (the button is gated on validity) but fail loudly on
        // the row rather than send a known-bad title.
        updateRow(row.key, { status: "failed", message: "Please give this file a valid title." })
        continue
      }

      updateRow(row.key, { status: "creating", message: null })
      try {
        const id: string = await importOneFile({
          token,
          file: row.file,
          title: trimmedTitle,
          folder,
        })
        createdIds.push(id)
        updateRow(row.key, { status: "created", message: null, createdId: id })
      } catch (caught: unknown) {
        // A library / no-scene file gives the fixed message; anything else
        // (including a 413 over-size) gives the backend or error message. The
        // run continues to the next file regardless (Requirement 16.5).
        const message: string =
          caught instanceof ImportRejectedError
            ? caught.message
            : caught instanceof Error
              ? caught.message
              : "Failed to import this file."
        const status: ImportRowStatus =
          caught instanceof ImportRejectedError ? "rejected" : "failed"
        updateRow(row.key, { status, message })
      }
    }

    setImporting(false)

    if (createdIds.length === 0) {
      // Nothing imported (every file rejected or failed): keep the dialog open
      // with the reasons shown; do not close or navigate.
      toast.error("No files were imported. See the reasons listed for each file.")
      return
    }

    // Several files: summary toast here; one file: the parent navigates to it.
    if (createdIds.length > 1) {
      toast.success(
        `Imported ${createdIds.length} of ${toProcess.length} diagram${
          toProcess.length === 1 ? "" : "s"
        }`,
      )
    }
    onImported({ createdIds, folder })
  }, [importableRows, getAccessToken, folder, updateRow, onImported])

  const stepDisabled: boolean = disabled || importing

  return (
    <div className="flex flex-col gap-4">
      {/* Drop zone + picker. The zone is a labelled button so keyboard and
          screen-reader users can open the picker; drag and drop is a visual
          enhancement on top. */}
      <div
        {...getRootProps({
          className: isDragActive
            ? "flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-primary bg-accent p-6 text-center"
            : "flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-border p-6 text-center",
          role: "group",
          "aria-label": "Import files drop zone",
        })}
      >
        <input {...getInputProps()} />
        <Upload className="size-6 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          {isDragActive
            ? "Drop the files to add them"
            : "Drag Excalidraw files here, or"}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={open} disabled={stepDisabled}>
          Choose files
        </Button>
        <p className="text-xs text-muted-foreground">
          Accepts .excalidraw, .svg, and .png files exported from Excalidraw.
        </p>
      </div>

      {/* Per-file rows: editable title, status, and any rejection/failure
          reason (Requirement 16.3, 16.5). */}
      {rows.length > 0 && (
        <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto" aria-label="Files to import">
          {rows.map((row: ImportRow): React.JSX.Element => {
            const titleError: string | null =
              row.status === "rejected" ? null : validateTitle(row.title)
            const isRejected: boolean = row.status === "rejected"
            return (
              <li
                key={row.key}
                className="flex flex-col gap-1 rounded-md border border-border px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <StatusIcon status={row.status} />
                  <Input
                    value={row.title}
                    onChange={(event) => handleTitleChange(row.key, event.target.value)}
                    aria-label={`Title for ${row.file.name}`}
                    aria-invalid={titleError !== null}
                    disabled={stepDisabled || isRejected || row.status === "created"}
                    className="h-8"
                  />
                </div>
                <p className="pl-6 text-xs text-muted-foreground">{row.file.name}</p>
                {/* The rejection/failure reason, or an inline title error. */}
                {row.message !== null && (
                  <p
                    role="alert"
                    className={
                      row.status === "created"
                        ? "pl-6 text-xs text-muted-foreground"
                        : "pl-6 text-xs text-destructive"
                    }
                  >
                    {row.message}
                  </p>
                )}
                {row.message === null && titleError !== null && (
                  <p role="alert" className="pl-6 text-xs text-destructive">
                    {titleError}
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => void handleImport()}
          disabled={stepDisabled || importableRows.length === 0 || !allTitlesValid}
        >
          {importing && <Loader2 className="size-4 animate-spin" data-icon="inline-start" />}
          {importing
            ? "Importing…"
            : importableRows.length <= 1
              ? "Import"
              : `Import ${importableRows.length} files`}
        </Button>
      </div>
    </div>
  )
}

/**
 * The status icon for an import row, matching its lifecycle state.
 *
 * @param props - Component props.
 * @param props.status - The row's current status.
 * @returns A small icon conveying the status (with an accessible label).
 */
function StatusIcon({ status }: { status: ImportRowStatus }): React.JSX.Element {
  switch (status) {
    case "creating":
      return <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-label="Creating" />
    case "created":
      return <CheckCircle2 className="size-3.5 text-green-600" aria-label="Created" />
    case "failed":
    case "rejected":
      return <XCircle className="size-3.5 text-destructive" aria-label="Not imported" />
    case "waiting":
    default:
      return <span className="size-3.5" aria-label="Waiting" />
  }
}
